import React from 'react';
import { srcSetDe } from './Img';

/**
 * Fondo de los heroes, resuelto para pantallas angostas.
 *
 * ── El problema ──────────────────────────────────────────────────────────
 * Todos los heroes con imagen de fondo la pintan con `cover` dentro de una
 * caja de alto FIJO pensada para escritorio. `cover` escala hasta tapar las
 * dos medidas, así que en una pantalla angosta manda el alto: una pieza de
 * 1920×982 dentro de una caja de 390×1000 se dibuja a 1955×1000 y en pantalla
 * entra el 20% del ancho. Si la pieza es una foto de ambiente da igual —es
 * decorativa—, pero si trae el texto adentro (promociones, precios, marcas)
 * el recorte se come justo lo que había que leer.
 *
 * Medido el 21-sep-2026 sobre los 105 tenants: 31 nodos de 24 clientes
 * muestran menos del 60% del ancho de su imagen a 390px.
 *
 * ── La solución ──────────────────────────────────────────────────────────
 * Cuatro perillas por nodo, apagadas por defecto. Un nodo que no las trae se
 * dibuja exactamente como siempre, en escritorio y en móvil.
 *
 *  - `mobileFit`      cómo entra la imagen abajo de `md`
 *  - `mobileImage`    pieza alternativa para móvil (escritorio no cambia)
 *  - `mobilePosition` punto focal cuando igual recorta
 *  - `mobileHeight`   alto de la caja en móvil
 *
 * ── Por qué CSS y no JavaScript ──────────────────────────────────────────
 * Todo se resuelve con una media query y `<picture>`. No hay medición, así
 * que no hay salto de layout, ni diferencia entre servidor y cliente, ni las
 * trampas del canvas del Builder (es un iframe y el código corre en el
 * documento de arriba: un `ResizeObserver` creado en otro realm no avisa).
 * La media query se evalúa contra el documento donde vive el elemento, que en
 * el Builder es el iframe — o sea que el selector de dispositivo funciona solo.
 */

/** Desde acá la caja del hero vuelve a la de escritorio. El mismo corte que usa el resto del repo. */
export const CORTE_MOVIL = 768;

/**
 * Cómo entra la imagen de fondo en una pantalla angosta.
 *
 *  - `cover`   la tapa entera recortando lo que sobre. Es lo de siempre.
 *  - `contain` entra completa dentro del alto de móvil y quedan franjas.
 *              **Necesita `mobileHeight`**: sin eso no hay "alto que respetar"
 *              en móvil y se comporta como `adapt`. Ver abajo.
 *  - `adapt`   la CAJA toma la proporción de la imagen: se ve entera y sin
 *              franjas. Es lo correcto para una pieza promocional.
 */
export type AjusteFondoMovil = 'cover' | 'contain' | 'adapt';

/**
 * Las props que un hero declara para ofrecer la perilla.
 *
 * Ninguna va en `craft.props`: los nodos ya publicados no las traen, salen
 * `undefined` y el hero se dibuja como siempre. Cero migración — el mismo
 * patrón que `mobileFullBleed` en `HeroWithVideoEmbed`.
 */
export interface PropsFondoMovil {
  /** Pieza alternativa para móvil. Vacío = se usa la de escritorio. */
  mobileImage?: string;
  /** Cómo entra la imagen abajo de `md`. Sin valor = `cover`, o sea lo de hoy. */
  mobileFit?: AjusteFondoMovil;
  /** Punto focal del recorte en móvil (`'50% 50%'`, `'left center'`, `'30% 20%'`). */
  mobilePosition?: string;
  /** Alto de la caja en móvil. En `adapt` funciona como tope. Ej.: `'420px'` o `420`. */
  mobileHeight?: string | number;
  /** Color de las franjas cuando la imagen no tapa toda la caja. */
  mobileBackdropColor?: string;
}

/**
 * ── Por qué `contain` no hereda el alto de escritorio ────────────────────────
 *
 * `contain` quiere decir "la imagen entera dentro del alto que elegiste". La
 * trampa es de dónde sale ese alto: si el nodo no trae `mobileHeight`, el único
 * que queda es el de ESCRITORIO, y un hero de 1000px en una pantalla de 390
 * deja la pieza (≈220px) flotando en 780px de vacío.
 *
 * Pasó en producción el 22-sep-2026: B2Autos subió sus tres piezas verticales,
 * eligió "Completa, respetando el alto" y el banner quedó con un hueco enorme
 * arriba y abajo. El componente hacía exactamente lo que decía; lo que estaba
 * mal era heredar para móvil una medida que se eligió mirando el escritorio.
 *
 * Desde acá, `contain` SIN `mobileHeight` se mide por la imagen, igual que
 * `adapt`. El alto configurado sólo manda en móvil si alguien lo eligió PARA
 * móvil. Quien sí quiere una franja de alto fijo lo sigue teniendo: pone el
 * alto de celular y `contain` lo respeta.
 */
const ATRIBUTO = 'data-fondo-movil';
const ATRIBUTO_IMG = 'data-fondo-movil-img';
const ATRIBUTO_ALTO = 'data-fondo-movil-alto';

/**
 * La hoja de estilos de las perillas.
 *
 * Va con `!important` a propósito: lo que hay que ganarle son declaraciones EN
 * LÍNEA (`style={{ height }}`), y una regla normal de hoja de estilos pierde
 * contra eso aunque esté dentro de una media query. Con `!important` la
 * cascada se da vuelta, y escritorio no se entera porque la media query ni
 * siquiera aplica desde 768px.
 *
 * Los selectores de la imagen son de DESCENDIENTE y no de hijo directo: el
 * `<picture>` que permite la imagen alternativa queda en el medio del árbol.
 * No estorba al layout —lleva `display: contents`, así que el `<img>` participa
 * de la grilla como si colgara de la caja— pero sí cuenta para el selector.
 */
const CSS_FONDO_MOVIL = `@media (max-width:${CORTE_MOVIL - 1}px){`
  // Punto focal: vale para cualquier modo que recorte.
  + `[${ATRIBUTO}] [${ATRIBUTO_IMG}]{object-position:var(--fondo-movil-pos,50% 50%)!important}`
  // Alto propio de móvil, para `cover` y `contain`.
  + `[${ATRIBUTO}][${ATRIBUTO_ALTO}]{height:var(--fondo-movil-alto)!important;`
  + `min-height:var(--fondo-movil-alto)!important}`
  // `contain` CON alto de móvil: la imagen entra entera en esa franja.
  + `[${ATRIBUTO}="contain"]{background-color:var(--fondo-movil-franja,transparent)}`
  + `[${ATRIBUTO}="contain"] [${ATRIBUTO_IMG}]{object-fit:contain!important;transform:none!important}`
  // `adapt` —y `contain` sin alto propio— apilan la caja en una sola celda: la
  // imagen pasa a estar EN FLUJO y le dicta el alto. El contenido comparte la
  // celda y, si es más alto que la imagen, la que crece es la celda: nunca se
  // desborda ni sobra hueco.
  + `[${ATRIBUTO}="adapt"],[${ATRIBUTO}="contain"]:not([${ATRIBUTO_ALTO}])`
  + `{display:grid!important;height:auto!important;`
  + `min-height:0!important;align-items:center;justify-items:stretch;`
  + `background-color:var(--fondo-movil-franja,transparent)}`
  + `[${ATRIBUTO}="adapt"]>*,[${ATRIBUTO}="contain"]:not([${ATRIBUTO_ALTO}])>*{grid-area:1/1}`
  + `[${ATRIBUTO}="adapt"] [${ATRIBUTO_IMG}],`
  + `[${ATRIBUTO}="contain"]:not([${ATRIBUTO_ALTO}]) [${ATRIBUTO_IMG}]`
  + `{position:static!important;grid-area:1/1;`
  + `width:100%!important;height:auto!important;max-height:var(--fondo-movil-alto,none);`
  + `object-fit:contain!important;`
  // El zoom lento del carrusel agranda la imagen sin agrandar la caja, así que
  // acá volvería a recortar justo lo que se quiso salvar.
  + `transform:none!important}`
  // El envoltorio de la sección puede traer un PISO de alto propio: es el alto
  // manual que el dealer movió desde la barra (`_alto` → `min-height` + una
  // fila `1fr`). Ese número se eligió mirando el escritorio y, como el hero es
  // el ítem de esa fila, se estira hasta él: la caja volvía a medir 800px por
  // más que la imagen pidiera 211 y el hueco seguía ahí.
  //
  // Se afloja SÓLO para el envoltorio de un hero que pidió ajuste móvil —de eso
  // se encarga `:has(> …)`—, así ninguna otra sección de ningún otro cliente se
  // entera. No se toca `_alto`: sigue mandando en escritorio, que es donde se
  // eligió.
  + `:has(>[${ATRIBUTO}="adapt"]),`
  + `:has(>[${ATRIBUTO}="contain"]:not([${ATRIBUTO_ALTO}]))`
  + `{min-height:0!important}`
  + `}`;

/** Hoja de estilos de las perillas. Se pinta sólo si el nodo las usa. */
export const EstilosFondoMovil = () => (
  <style dangerouslySetInnerHTML={{ __html: CSS_FONDO_MOVIL }} />
);

/** `true` si el nodo configuró algo de móvil. Con `false` no se toca nada. */
export const usaFondoMovil = (p: PropsFondoMovil): boolean =>
  !!(p.mobileImage || p.mobileFit || p.mobilePosition || p.mobileHeight);

/**
 * Los atributos y variables que van en la CAJA del hero (el contenedor que
 * hoy lleva `height` y `position: relative`).
 *
 * El `style` que devuelve son sólo variables: se mezcla con el del hero sin
 * pisarle nada. Lo que cambia el layout vive en la media query.
 */
export const atributosCajaFondoMovil = (
  p: PropsFondoMovil,
): { [k: string]: string | React.CSSProperties } => {
  if (!usaFondoMovil(p)) return {};
  const vars: Record<string, string> = {};
  if (p.mobilePosition) vars['--fondo-movil-pos'] = p.mobilePosition;
  // El panel guarda un número pelado; CSS necesita la unidad.
  if (p.mobileHeight) vars['--fondo-movil-alto'] = /^\d+(\.\d+)?$/.test(String(p.mobileHeight))
    ? `${p.mobileHeight}px`
    : String(p.mobileHeight);
  if (p.mobileBackdropColor) vars['--fondo-movil-franja'] = p.mobileBackdropColor;
  return {
    [ATRIBUTO]: p.mobileFit || 'cover',
    ...(p.mobileHeight ? { [ATRIBUTO_ALTO]: '' } : {}),
    style: vars as React.CSSProperties,
  };
};

export interface PropsFondoDelHero {
  /** La imagen de escritorio. Vacío = no se pinta nada (ni un ícono roto). */
  imagen?: string;
  /** Pieza alternativa para móvil. */
  mobileImage?: string;
  /** Estilo que el hero le aplicaba al fondo (zoom del carrusel, opacidad, z-index…). */
  style?: React.CSSProperties;
  /** Marca la imagen como prioritaria: es la que suele ganar el LCP. */
  prioridad?: boolean;
}

/**
 * La capa de fondo de un hero.
 *
 * Reemplaza al `background-image` que antes vivía en la caja o en un `div`
 * absoluto. En escritorio pinta exactamente lo mismo —un `<img>` que ocupa
 * toda la caja con `object-fit: cover` es el equivalente exacto de
 * `background-size: cover; background-position: center`—, pero al ser un
 * elemento de verdad puede cambiar de archivo con `<picture>` y salir del
 * posicionamiento absoluto en móvil, que es lo que `adapt` necesita.
 */
export const FondoDelHero = ({
  imagen,
  mobileImage,
  style,
  prioridad,
}: PropsFondoDelHero) => {
  if (!imagen) return null;
  return (
    <picture style={{ display: 'contents' }}>
      {mobileImage ? (
        <source
          media={`(max-width:${CORTE_MOVIL - 1}px)`}
          // Variantes por ancho del optimizador de Next (ver `srcSetDe`); si la
          // URL no es optimizable, la de siempre.
          srcSet={srcSetDe(mobileImage) ?? mobileImage}
          sizes='100vw'
        />
      ) : null}
      <img
        {...{ [ATRIBUTO_IMG]: '' }}
        src={imagen}
        // El fondo del hero ocupa el ancho de la pantalla: se pide la variante
        // que corresponde a ese ancho en vez del original (2400 px para una
        // caja de 427 en algunos tenants).
        srcSet={srcSetDe(imagen)}
        sizes={srcSetDe(imagen) ? '100vw' : undefined}
        alt=''
        aria-hidden='true'
        draggable={false}
        decoding='async'
        fetchPriority={prioridad ? 'high' : undefined}
        // Un `background-image` que ya no existe no se ve; un `<img>` roto
        // muestra el ícono del navegador. Hay URLs muertas guardadas en
        // páginas viejas, así que un fallo se esconde y queda como antes.
        //
        // Antes de esconderla: si lo que falló fue una VARIANTE del optimizador
        // (hay srcset), se vuelve al original —el `<source>` móvil incluido—,
        // que es exactamente lo que se servía antes. Sin esto, un objeto que el
        // optimizador no pueda transformar dejaría el hero sin fondo.
        onError={(e) => {
          const img = e.currentTarget;
          if (img.getAttribute('srcset')) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            const source = img.parentElement?.querySelector('source');
            if (source && mobileImage) source.setAttribute('srcset', mobileImage);
            return;
          }
          img.style.visibility = 'hidden';
        }}
        onLoad={(e) => { e.currentTarget.style.visibility = ''; }}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 0,
          ...style,
        }}
      />
    </picture>
  );
};
